import { type Offer, type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { addHours, differenceInHours, differenceInMinutes, parseISO } from "date-fns";

import type { OpenBundleShift, ShiftBundle, ShiftBundlesConfig, ShiftTimeData } from "./types";
import {
  OVERWORKING_WINDOW_SIZE_IN_HOURS,
  wouldProposedShiftCauseOverworking,
} from "./wouldProposedShiftCauseOverworking";

function createBundleId(workplaceId: string, qualification: string): string {
  return `bundle-${workplaceId}-${qualification}`;
}

export function calculateRequiredShiftCount(
  availableShiftCount: number,
  config: Extract<ShiftBundlesConfig, { enabled: true }>
): number {
  const { percentageOfAvailableShiftsRequiredToBundle, maxRequiredShifts, minimumBundleSize } =
    config;
  const raw = Math.ceil(availableShiftCount * percentageOfAvailableShiftsRequiredToBundle);
  return Math.min(Math.max(raw, minimumBundleSize), maxRequiredShifts);
}

/**
 * Groups shifts into bundles by (workplaceId, qualification), filtering out shifts that
 * would cause overworking (>17 hours within any 24-hour window)
 *
 * A shift is eligible for bundling if:
 * 1. Its workplace is in the `eligibleWorkplaceIds` set
 * 2. It starts more than `leadTimeInHours` from now
 * 3. It doesn't cause overworking when combined with booked shifts
 *
 * Each shift gets its own bundle with filtered companion shifts (excluding those that
 * would cause overworking if booked together. A shift only gets a bundle if it has at
 * least `minimumBundleSize` compatible companions.
 *
 * Returns a map of shiftId → ShiftBundle.
 */
const MINUTES_PER_HOUR = 60;

function isShiftWithOffer(shift: OpenShift): shift is OpenBundleShift {
  return isDefined(shift.relationships.offer?.data.id);
}

function calculateBundleTotalPayInMinorUnits(
  bundleShifts: OpenBundleShift[],
  offersMap: Map<string, Offer>
): number | undefined {
  let totalMinorUnits = 0;

  for (const shift of bundleShifts) {
    const offerId = shift.relationships.offer.data.id;

    const offer = offersMap.get(offerId);
    if (!offer) {
      return undefined;
    }

    const start = parseISO(shift.attributes.start);
    const end = parseISO(shift.attributes.end);
    const durationInHours = differenceInMinutes(end, start) / MINUTES_PER_HOUR;
    totalMinorUnits += durationInHours * offer.attributes.pay.amountInMinorUnits;
  }

  return Math.round(totalMinorUnits);
}

export function groupShiftsIntoBundles({
  shifts,
  config,
  eligibleWorkplaceIds,
  bookedShifts,
  offersMap = new Map(),
}: {
  shifts: OpenShift[];
  config: ShiftBundlesConfig;
  eligibleWorkplaceIds: Set<string>;
  bookedShifts: ShiftTimeData[];
  offersMap?: Map<string, Offer>;
}): Map<string, ShiftBundle> {
  if (!config.enabled) {
    return new Map();
  }

  const now = new Date();
  const { leadTimeInHours, minimumBundleSize } = config;

  const groups = new Map<
    string,
    { workplaceId: string; qualification: string; shifts: OpenBundleShift[] }
  >();

  for (const shift of shifts) {
    const workplaceId = shift.relationships.workplace.data.id;

    if (!eligibleWorkplaceIds.has(workplaceId)) {
      continue;
    }

    const shiftStart = parseISO(shift.attributes.start);
    const hoursUntilStart = differenceInHours(shiftStart, now);

    if (hoursUntilStart < leadTimeInHours) {
      continue;
    }

    if (!isShiftWithOffer(shift)) {
      continue;
    }

    const { qualification } = shift.attributes;
    const key = createBundleId(workplaceId, qualification);

    const existing = groups.get(key);
    if (existing) {
      existing.shifts.push(shift);
    } else {
      groups.set(key, { workplaceId, qualification, shifts: [shift] });
    }
  }

  for (const [bundleId, group] of groups) {
    const eligibleShifts = group.shifts.filter((shift) => {
      const shiftData = {
        start: shift.attributes.start,
        end: shift.attributes.end,
      };
      return !wouldProposedShiftCauseOverworking(shiftData, bookedShifts);
    });
    group.shifts = eligibleShifts;

    if (eligibleShifts.length < minimumBundleSize) {
      groups.delete(bundleId);
    }
  }

  const bundlesByShiftId = new Map<string, ShiftBundle>();

  for (const group of groups.values()) {
    for (const shift of group.shifts) {
      const shiftData = {
        start: shift.attributes.start,
        end: shift.attributes.end,
      };

      // Find companion shifts that DON'T cause overworking with this shift
      const compatibleShifts = group.shifts.filter((companionShift) => {
        if (companionShift.id === shift.id) {
          return true; // Always include the shift itself
        }

        const companionData = {
          start: companionShift.attributes.start,
          end: companionShift.attributes.end,
        };

        const shiftStart = parseISO(shiftData.start);
        const companionStart = parseISO(companionData.start);
        const companionEnd = parseISO(companionData.end);
        const windowEnd = addHours(shiftStart, OVERWORKING_WINDOW_SIZE_IN_HOURS);

        // If companion starts after our 24-hour window ends, it's compatible
        if (companionStart >= windowEnd) {
          return true;
        }

        // If companion ends before our 24-hour window starts, it's compatible
        const windowStart = shiftStart;
        if (companionEnd <= windowStart) {
          return true;
        }

        // Otherwise, check if adding companion to (shift + booked shifts) would cause overworking
        return !wouldProposedShiftCauseOverworking(companionData, [shiftData, ...bookedShifts]);
      });

      // Only create bundle if it meets minimum size requirement
      if (compatibleShifts.length < minimumBundleSize) {
        // Skip this shift - it doesn't have enough compatible companions
        // But continue processing other shifts in the group
        continue;
      }

      // Create bundle for this shift
      const bundle: ShiftBundle = {
        workplaceId: group.workplaceId,
        qualification: group.qualification,
        shifts: compatibleShifts,
        totalShiftCount: compatibleShifts.length,
        requiredShiftCount: calculateRequiredShiftCount(compatibleShifts.length, config),
        totalPayInMinorUnits: calculateBundleTotalPayInMinorUnits(compatibleShifts, offersMap),
      };

      bundlesByShiftId.set(shift.id, bundle);
    }
  }

  return bundlesByShiftId;
}
